import type {
	ChatIssuesReport as ChatIssuesReportResult,
	MembershipSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useMemo, useState } from 'react';

import {
	createChatIssueClassification,
	deleteChatIssueClassification,
	getChatIssuesReport,
} from '../api.js';

function today(): string {
	const parts = new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	}).formatToParts(new Date());
	const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
	return `${value.year}-${value.month}-${value.day}`;
}

export function ChatIssuesReport({ membership }: { membership: MembershipSummary }) {
	const [date, setDate] = useState(today);
	const [result, setResult] = useState<ChatIssuesReportResult>();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const [classificationName, setClassificationName] = useState('');
	const [classificationError, setClassificationError] = useState<string>();
	const [savingClassification, setSavingClassification] = useState(false);
	const [deletingClassificationId, setDeletingClassificationId] = useState<string>();
	const [refresh, setRefresh] = useState(0);
	const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void getChatIssuesReport(membership.tenantId, { date, timezone })
			.then((response) => {
				if (current) setResult(response);
			})
			.catch((caught: unknown) => {
				if (current)
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the chat classification report',
					);
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [date, membership.tenantId, refresh, timezone]);

	async function addClassification(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const name = classificationName.trim();
		if (!name) return;
		setSavingClassification(true);
		setClassificationError(undefined);
		try {
			await createChatIssueClassification(membership.tenantId, { name });
			setClassificationName('');
			setRefresh((value) => value + 1);
		} catch (caught) {
			setClassificationError(
				caught instanceof Error ? caught.message : 'Could not add the classification',
			);
		} finally {
			setSavingClassification(false);
		}
	}

	async function removeClassification(classificationId: string): Promise<void> {
		setDeletingClassificationId(classificationId);
		setClassificationError(undefined);
		try {
			await deleteChatIssueClassification(membership.tenantId, classificationId);
			setRefresh((value) => value + 1);
		} catch (caught) {
			setClassificationError(
				caught instanceof Error ? caught.message : 'Could not delete the classification',
			);
		} finally {
			setDeletingClassificationId(undefined);
		}
	}

	const overview = result?.overview;
	const metrics = overview
		? [
				['Closed chats', overview.totalClosedChats.toLocaleString()],
				['Analysed', overview.analyzedChats.toLocaleString()],
				['Coverage', `${overview.coveragePercent.toFixed(1)}%`],
				['Resolved', `${overview.resolvedPercent.toFixed(1)}%`],
				[
					'Avg confidence',
					overview.averageConfidence === undefined
						? '—'
						: `${Math.round(overview.averageConfidence * 100)}%`,
				],
				[
					'Pending / failed',
					`${overview.pendingAnalyses.toLocaleString()} / ${overview.failedAnalyses.toLocaleString()}`,
				],
			]
		: [];

	return (
		<section className="reports-workspace" aria-labelledby="reports-heading">
			<header className="reports-heading">
				<div>
					<h2 id="reports-heading">Chat classification</h2>
					<p>See why customers contacted {membership.tenantName} on one day.</p>
				</div>
			</header>
			<div className="reports-toolbar chat-issues-toolbar">
				<label>
					<span>Date</span>
					<input
						type="date"
						value={date}
						onChange={(event) => setDate(event.target.value)}
					/>
				</label>
			</div>
			{error && <div className="alert error reports-alert">{error}</div>}
			<div className="report-metrics" aria-busy={loading}>
				{metrics.map(([metricLabel, value]) => (
					<div className="report-metric" key={metricLabel}>
						<span>
							<strong>{value}</strong>
							<small>{metricLabel}</small>
						</span>
					</div>
				))}
				{loading &&
					!result &&
					Array.from({ length: 6 }, (_, index) => (
						<div className="report-metric report-metric-loading" key={index} />
					))}
			</div>

			<section
				className="settings-card integration-panel chat-issue-classification-panel"
				aria-labelledby="classifications-settings-heading"
			>
				<div className="integration-panel-heading">
					<div>
						<h3 className="panel-title" id="classifications-settings-heading">
							Classifications
						</h3>
						<p>
							AI must choose one of these classifications. If none fit, it uses Other.
						</p>
					</div>
				</div>
				<form className="chat-issue-classification-form" onSubmit={addClassification}>
					<label>
						<span>New classification</span>
						<input
							maxLength={120}
							placeholder="For example, Parcel tracking"
							value={classificationName}
							onChange={(event) => setClassificationName(event.target.value)}
						/>
					</label>
					<button
						className="primary-button"
						disabled={!classificationName.trim() || savingClassification}
						type="submit"
					>
						{savingClassification ? 'Adding…' : 'Add classification'}
					</button>
				</form>
				{classificationError && <div className="alert error">{classificationError}</div>}
				<div className="chat-issue-classification-list">
					{result?.classifications.map((classification) => (
						<div className="chat-issue-classification-item" key={classification.id}>
							<span>{classification.name}</span>
							<button
								className="danger-button"
								aria-label={`Delete ${classification.name}`}
								disabled={deletingClassificationId === classification.id}
								onClick={() => void removeClassification(classification.id)}
								type="button"
							>
								{deletingClassificationId === classification.id
									? 'Deleting…'
									: 'Delete'}
							</button>
						</div>
					))}
					<div className="chat-issue-classification-item fixed">
						<span>Other</span>
						<small>Fallback</small>
					</div>
				</div>
			</section>

			<section className="agent-report-section" aria-labelledby="classifications-heading">
				<header className="table-section-heading">
					<h3 id="classifications-heading">Classifications</h3>
				</header>
				<div
					className="table-card chat-issues-table-scroll"
					role="table"
					aria-busy={loading}
				>
					<div className="chat-issues-summary-row standard-table-head" role="row">
						<div role="columnheader">Classification</div>
						<div role="columnheader">Chats</div>
						<div role="columnheader">Share</div>
						<div role="columnheader">Resolved</div>
					</div>
					{result?.issues.map((issue) => (
						<div
							className="table-row chat-issues-summary-row"
							key={issue.issue}
							role="row"
						>
							<div role="cell">
								<strong>{issue.issue}</strong>
							</div>
							<div role="cell">{issue.chatCount.toLocaleString()}</div>
							<div role="cell">{issue.sharePercent.toFixed(1)}%</div>
							<div role="cell">{issue.resolutionRate.toFixed(1)}%</div>
						</div>
					))}
					{!loading && !result?.issues.length && (
						<div className="agent-report-empty" role="row">
							<div role="cell">
								<strong>No analysed chats</strong>
								<span>
									Enable AI conversation insights on the Integration page to
									analyse newly completed chats.
								</span>
							</div>
						</div>
					)}
				</div>
			</section>
		</section>
	);
}
